<template>
  <div class="modal fade" id="billetagePaiementModal" tabindex="-1" 
    aria-labelledby="billetagePaiementModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title text-black">
            <i class="fa fa-calculator me-2"></i>Billetage de Vente
          </h5>
          <button type="button" class="btn-close btn-close-white" 
            data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        
        <div class="modal-body">
          <div class="row">
            <div class="col-md-6 mb-3">
              <div class="billetage-card billetage-encaissement">
                <div class="billetage-card-header">
                  <h4 class="billetage-card-title">Billetage Encaissement</h4>
                  <h3 class="billetage-card-amount">{{ montantTotalEncaissement || 0 }} Fcfa</h3>
                </div>
                <div class="billetage-card-body">
                  <div class="billetage-table-header">
                    <div class="col-header">Monnaie</div>
                    <div class="col-header">Quantité</div>
                    <div class="col-header">Montant</div>
                  </div>
                  <hr class="my-2" />
                  <div v-for="(billetage, index) in billetageList" :key="'enc-' + index" 
                    class="billetage-row">
                    <div class="billetage-col">
                      <input 
                        v-model="billetage.libelle" 
                        readonly
                        class="form-control form-control-sm billetage-monnaie encaissement"
                        @click="incrementBilletage(billetage, 'encaissement')" 
                        title="Cliquer pour incrémenter" />
                    </div>
                    <div class="billetage-col">
                      <input 
                        type="number" 
                        min="0" 
                        v-model.number="billetage.qteBillet"
                        @input="handleBilletageInput($event, billetage, 'encaissement')"
                        @keypress="restrictInput($event)" 
                        class="form-control form-control-sm" 
                        placeholder="0" />
                    </div>
                    <div class="billetage-col">
                      <input 
                        type="text" 
                        v-model="billetage.montant" 
                        readonly 
                        class="form-control form-control-sm" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Billetage Reliquat -->
            <div class="col-md-6 mb-3">
              <div class="billetage-card billetage-sortie">
                <div class="billetage-card-header">
                  <h4 class="billetage-card-title">Reliquat à Rendre</h4>
                  <h3 class="billetage-card-amount">{{ reliquatCalcule || 0 }} Fcfa</h3>
                </div>
                <div class="billetage-card-body">
                  <div class="billetage-table-header">
                    <div class="col-header">Monnaie</div>
                    <div class="col-header">Quantité</div>
                    <div class="col-header">Montant</div>
                  </div>
                  <hr class="my-2" />
                  <div v-for="(billetage, index) in billetageSortieList" :key="'sort-' + index" 
                    class="billetage-row">
                    <div class="billetage-col">
                      <input 
                        v-model="billetage.libelle" 
                        readonly
                        class="form-control form-control-sm billetage-monnaie sortie"
                        @click="incrementBilletage(billetage, 'sortie')" 
                        title="Cliquer pour incrémenter" />
                    </div>
                    <div class="billetage-col">
                      <input 
                        type="number" 
                        min="0" 
                        v-model.number="billetage.qteBillet"
                        @input="handleBilletageInput($event, billetage, 'sortie')"
                        @keypress="restrictInput($event)" 
                        class="form-control form-control-sm" 
                        placeholder="0" />
                    </div>
                    <div class="billetage-col">
                      <input 
                        type="text" 
                        v-model="billetage.montant" 
                        readonly 
                        class="form-control form-control-sm" />
                    </div>
                  </div>
                  <div v-if="montantTotalSortie > reliquatCalcule" 
                    class="alert alert-danger mt-3 py-2 mb-0">
                    <i class="fa fa-exclamation-triangle me-2"></i>
                    <small>Le billetage de sortie ({{ montantTotalSortie }} Fcfa) dépasse le reliquat ({{ reliquatCalcule }} Fcfa).</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="billetageError" class="alert alert-danger mt-2">
            <i class="fa fa-exclamation-triangle me-2"></i>{{ billetageError }}
          </div>
          <div v-if="reliquatNonRendu > 0" class="alert alert-warning mt-2">
            <i class="fa fa-exclamation-triangle me-2"></i>
            Reliquat non rendu au client: {{ reliquatNonRendu }} Fcfa
          </div>
        </div>

        <div class="modal-footer">
          <button 
            type="button" 
            class="btn btn-primary" 
            @click="valider" 
            data-bs-dismiss="modal"
            :disabled="montantTotalEncaissement < totalttc || montantTotalSortie > reliquatCalcule">
            <i class="fa fa-check me-2"></i>Valider
          </button>
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
            <i class="fa fa-times me-2"></i>Annuler
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, computed, watch } from 'vue';

export interface BilletageForm {
  libelle: string;
  qteBillet: number;
  montant: number;
  monnaie: number;
  valueAct: number;
}

export default defineComponent({
  name: 'BilletageModal',
  props: {
    billetageList: {
      type: Array as PropType<BilletageForm[]>,
      required: true
    },
    billetageSortieList: {
      type: Array as PropType<BilletageForm[]>,
      required: true
    },
    totalttc: {
      type: Number,
      required: true
    },
    billetageError: {
      type: String,
      default: ''
    }
  },
  emits: ['update:billetageList', 'update:billetageSortieList', 'validate', 'update:montantRecu'],
  setup(props, { emit }) {
    
    const montantTotalEncaissement = computed(() => {
      return props.billetageList.reduce((total, billetage) => {
        return total + (billetage.montant || 0);
      }, 0);
    });

    const montantTotalSortie = computed(() => {
      return props.billetageSortieList.reduce((total, billetage) => {
        return total + (billetage.montant || 0);
      }, 0);
    });

    const reliquatCalcule = computed(() => {
      if (!montantTotalEncaissement.value || !props.totalttc) {
        return 0;
      }
      return montantTotalEncaissement.value - props.totalttc;
    });

    const reliquatNonRendu = computed(() => {
      const sortie = montantTotalSortie.value || 0;
      const reliquat = reliquatCalcule.value || 0;
      return reliquat - sortie >= 0 ? reliquat - sortie : 0;
    });

    const incrementBilletage = (billetage: BilletageForm, type: 'encaissement' | 'sortie') => {
      billetage.qteBillet += 1;
      billetage.montant = billetage.qteBillet * billetage.valueAct;
      
      if (type === 'encaissement') {
        emit('update:billetageList', props.billetageList);
      } else {
        emit('update:billetageSortieList', props.billetageSortieList);
      }
    };

    const handleBilletageInput = (event: Event, billetage: BilletageForm, type: 'encaissement' | 'sortie') => {
      const target = event.target as HTMLInputElement;
      let value = parseInt(target.value) || 0;

      if (value < 0) value = 0;

      billetage.qteBillet = value;
      billetage.montant = billetage.qteBillet * billetage.valueAct;

      if (type === 'encaissement') {
        emit('update:billetageList', props.billetageList);
      } else {
        emit('update:billetageSortieList', props.billetageSortieList);
      }
    };

    const restrictInput = (event: KeyboardEvent) => {
      const charCode = event.which ? event.which : event.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        event.preventDefault();
      }
    };

    const valider = () => {
      if (!montantTotalEncaissement.value || montantTotalEncaissement.value < props.totalttc) {
        return;
      }

      if (montantTotalSortie.value > reliquatCalcule.value) {
        return;
      }

      emit('validate', {
        montantTotalEncaissement: montantTotalEncaissement.value,
        montantTotalSortie: montantTotalSortie.value,
        reliquatNonRendu: reliquatNonRendu.value,
        billetageList: props.billetageList,
        billetageSortieList: props.billetageSortieList
      });
    };

    // Émettre le montant reçu quand il change
    watch(montantTotalEncaissement, (newVal) => {
      emit('update:montantRecu', newVal);
    });

    return {
      montantTotalEncaissement,
      montantTotalSortie,
      reliquatCalcule,
      reliquatNonRendu,
      incrementBilletage,
      handleBilletageInput,
      restrictInput,
      valider
    };
  }
});
</script>

<style scoped>


/* Cards */
.billetage-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.billetage-encaissement {
  border: 2px solid #3b82f6;
}

.billetage-sortie {
  border: 2px solid #ef4444;
}

.billetage-card-header {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 1rem 1.25rem;
  min-height: 90px;
  justify-content: center;
}

.billetage-encaissement .billetage-card-header {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}

.billetage-sortie .billetage-card-header {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.billetage-card-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: white;
  margin: 0;
}

.billetage-card-amount {
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin: 0;
}

.billetage-card-body {
  padding: 1rem 1.25rem;
  background: white;
}

/* Table */
.billetage-table-header {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  color: #6b7280;
}

.col-header {
  text-align: center;
}

.billetage-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
  margin-bottom: 10px;
  align-items: center;
}

.billetage-col {
  display: flex;
}

/* Input Monnaie */
.billetage-monnaie {
  font-weight: 700;
  text-align: center;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
}

.billetage-monnaie.encaissement {
  background-color: #dbeafe;
  border: 1px solid #3b82f6;
  color: #1e40af;
}

.billetage-monnaie.encaissement:hover {
  background-color: #bfdbfe;
  transform: scale(1.02);
}

.billetage-monnaie.sortie {
  background-color: #fee2e2;
  border: 1px solid #ef4444;
  color: #991b1b;
}

.billetage-monnaie.sortie:hover {
  background-color: #fecaca;
  transform: scale(1.02);
}

/* Inputs */
.form-control-sm {
  font-size: 0.875rem;
  padding: 0.375rem 0.5rem;
}

input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
}

/* Alerts */
.alert {
  border-radius: 8px;
  font-size: 0.9rem;
}

/* Footer */
.modal-footer {
  border-top: 1px solid #e5e7eb;
  padding: 1rem 1.5rem;
}

.modal-footer .btn {
  border-radius: 6px;
  padding: 0.5rem 1.5rem;
  font-weight: 500;
}

/* Responsive */
@media (max-width: 768px) {
  .modal-lg {
    max-width: 95%;
  }

  .billetage-card-header {
    min-height: 80px;
  }

  .billetage-card-title {
    font-size: 1rem;
  }

  .billetage-card-amount {
    font-size: 1.25rem;
  }

  .billetage-table-header {
    font-size: 0.8rem;
  }
}
</style>
